import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

// =============================================================================
// SearchSelect — a type-to-search combobox for form fields.
//
//   <SearchSelect
//     value={form.gl_account_code}
//     onChange={(code) => set('gl_account_code', code)}
//     options={glAccounts.map((a) => ({ code: a.code, name: a.name }))}
//     placeholder="— none —"
//   />
//
// Replaces a <select> where the list is long enough that scrolling it is the
// wrong interaction — a chart of accounts, a cost-centre master, a site list.
// An option is { code, name?, hint? }; `code` is what onChange receives, and
// searching matches BOTH code and name, so "5201" and "wages" both find the
// wages account.
//
// Distinct from components/PickerPopover: that one anchors to an arbitrary
// x/y from a canvas grid cell. This one anchors to itself and behaves like a
// form control — label, focus ring, keyboard, clearable.
//
// The list is position:fixed off the field's own rect rather than absolutely
// positioned, so a modal with overflow-y-auto cannot clip it.
// =============================================================================

const LIST_MAX_H = 288;   // max-h-72
const LIST_MIN_W = 220;   // readable option rows even under a narrow field

const SearchSelect = ({
  value,
  onChange,
  options = [],
  placeholder = '— none —',
  emptyText = 'No match.',
  clearable = true,
  clearLabel = '— none —',
  disabled = false,
  id,
  className = '',
  ariaLabel,
  // autoWidth: size the field to the text it is showing instead of a fixed
  // width. An <input> will not shrink-wrap in CSS, so this drives the `size`
  // attribute (width in characters) — the one thing that does. For chrome like
  // the top bar, where a fixed width either truncates a long site name or
  // leaves a gap after a short one.
  autoWidth = false,
  autoWidthMin = 10,   // characters, so a short label still reads as a field
  autoWidthMax = 40,   // stops one long name pushing the rest off the bar
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  // Stable per instance, so aria-controls/aria-activedescendant point somewhere
  // real and two SearchSelects on one form never collide.
  const listId = useRef(`ss-${Math.random().toString(36).slice(2, 9)}`).current;

  const selected = useMemo(
    () => options.find((o) => String(o.code) === String(value ?? '')),
    [options, value],
  );
  // `label` wins over code · name — for sentinel options ("— clear —") whose
  // stored value is machinery the user should never read.
  const labelOf = useCallback(
    (o) => (o ? (o.label ?? [o.code, o.name].filter((p) => p !== '' && p != null).join(' · ')) : ''),
    [],
  );

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    // Every term must appear somewhere, so "wage 52" narrows rather than widens.
    const terms = s.split(/\s+/);
    return options.filter((o) => {
      const hay = `${o.code} ${o.label || ''} ${o.name || ''} ${o.hint || ''}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [q, options]);

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flip above when there is not room below.
    const below = window.innerHeight - r.bottom;
    const flip = below < LIST_MAX_H && r.top > below;
    // The list gets a floor so an auto-width field showing a short label still
    // opens wide enough to read the options — but never wider than the space
    // left to its right, or it would run off screen.
    const width = Math.min(Math.max(r.width, LIST_MIN_W), window.innerWidth - r.left - 8);
    setRect({ left: r.left, width, top: flip ? undefined : r.bottom + 4, bottom: flip ? window.innerHeight - r.top + 4 : undefined });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Reposition rather than close: the field may sit in a scrolling modal.
    const onScroll = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  // Keep the highlighted row in view as the arrows move it.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector(`[data-idx="${active}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const openList = () => {
    if (disabled) return;
    setQ('');
    setActive(Math.max(0, shown.findIndex((o) => String(o.code) === String(value ?? ''))));
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.select());
  };

  const pick = (code) => {
    onChange(code);
    setOpen(false);
    setQ('');
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); openList(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (shown[active]) pick(shown[active].code);
    } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setQ(''); }
    else if (e.key === 'Tab') { setOpen(false); setQ(''); }
  };

  const shownText = open ? q : labelOf(selected);

  // `size` is in characters and the glyphs are proportional, so this is an
  // approximation — the +2 absorbs the drift and keeps the text off the chevron.
  const charSize = autoWidth
    ? Math.min(autoWidthMax, Math.max(autoWidthMin, (shownText || placeholder || '').length + 2))
    : undefined;

  const field = `${autoWidth ? 'w-auto' : 'w-full'} text-sm rounded-md border border-slate-300 dark:border-slate-600 `
    + 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 pl-2.5 pr-14 py-1.5 '
    + 'outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div ref={wrapRef} className={`relative ${autoWidth ? 'inline-block' : ''} ${className}`}>
      <input
        id={id}
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        // Closed, the field reads as the current selection; open, it is the
        // search box. One control, so there is nothing extra to click.
        value={shownText}
        size={charSize}
        placeholder={selected ? '' : placeholder}
        onChange={(e) => { setQ(e.target.value); setActive(0); if (!open) setOpen(true); }}
        onMouseDown={(e) => { if (!open) { e.preventDefault(); openList(); } }}
        onKeyDown={onKeyDown}
        className={field}
        autoComplete="off"
      />

      <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5 pointer-events-none">
        {clearable && selected && !open && (
          <button
            type="button" tabIndex={-1} title="Clear"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(''); }}
            className="pointer-events-auto p-0.5 rounded text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && rect && (
        <div
          ref={listRef}
          style={{ position: 'fixed', left: rect.left, top: rect.top, bottom: rect.bottom, width: rect.width, zIndex: 70 }}
          className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl overflow-hidden"
        >
          <div id={listId} role="listbox" className="max-h-72 overflow-y-auto py-0.5">
            {clearable && (
              <button
                type="button" data-idx={-1}
                onClick={() => pick('')}
                className="w-full text-left px-2.5 py-1.5 text-xs text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {clearLabel}
              </button>
            )}
            {shown.length === 0 && (
              <p className="m-0 px-2.5 py-3 text-xs text-slate-400">{emptyText}</p>
            )}
            {shown.map((o, i) => {
              const isSel = String(o.code) === String(value ?? '');
              return (
                <button
                  key={String(o.code)}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={isSel}
                  type="button"
                  data-idx={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o.code)}
                  className={`w-full flex items-baseline gap-1.5 px-2.5 py-1.5 text-left text-xs ${
                    i === active ? 'bg-slate-100 dark:bg-slate-700' : ''
                  } ${isSel ? 'font-semibold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-200'}`}
                >
                  {/* `label` replaces the pair outright. Otherwise a blank code
                      is a real option ("All sites"), not a gap — show its name
                      as the primary text rather than an empty mono column. */}
                  {o.label ? (
                    <span className="truncate">{o.label}</span>
                  ) : (
                    <>
                      {o.code !== '' && o.code !== null && o.code !== undefined && (
                        <span className="shrink-0 font-mono font-semibold">{o.code}</span>
                      )}
                      {o.name && (
                        <span className={`truncate ${o.code === '' ? '' : 'text-slate-500 dark:text-slate-400'}`}>{o.name}</span>
                      )}
                    </>
                  )}
                  {o.hint && <span className="ml-auto shrink-0 text-slate-400">{o.hint}</span>}
                  {isSel && <Check className="ml-auto shrink-0 w-3.5 h-3.5 text-emerald-500" />}
                </button>
              );
            })}
          </div>
          {options.length > 12 && (
            <div className="border-t border-slate-200 dark:border-slate-700 px-2.5 py-1 text-[10px] text-slate-400">
              {shown.length} of {options.length} · type to filter, ↑↓ to move, ↵ to pick
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
